import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mail, Send, RefreshCw, Calendar } from "lucide-react";

const WEEKDAYS = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

export default function EmailInbox() {
  const [data, setData] = useState({ items: [], resend_configured: false });
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewSubject, setPreviewSubject] = useState("");
  const [busy, setBusy] = useState(false);
  const [prefs, setPrefs] = useState({ enabled: true, weekday: 6, hour_utc: 9 });

  const load = async () => {
    const [ob, dp] = await Promise.all([api.get("/emails/outbox"), api.get("/me/digest-prefs")]);
    setData(ob.data); setPrefs(dp.data);
  };
  useEffect(() => { load(); }, []);

  const savePrefs = async (next) => {
    setPrefs(next);
    try { await api.put("/me/digest-prefs", next); toast.success("Preferences saved"); }
    catch { toast.error("Failed"); }
  };

  const preview = async (id) => {
    const { data } = await api.get(`/emails/outbox/${id}/preview`);
    setPreviewHtml(data.html); setPreviewSubject(data.subject);
  };

  const sendDigest = async () => {
    setBusy(true);
    try { await api.post("/emails/send-digest-now"); toast.success("Digest queued"); await load(); }
    catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const flush = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/emails/flush-queued");
      toast.success(`Flushed ${data.flushed || 0}`);
      await load();
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const kindColor = (k) => ({ welcome: "signal-buy", alert: "signal-wait", weekly_digest: "signal-hold" }[k] || "");
  const statusColor = (s) => ({ sent: "text-[hsl(var(--signal-buy))]", queued: "text-[hsl(var(--signal-hold))]", failed: "text-[hsl(var(--signal-wait))]" }[s] || "text-muted-foreground");

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-primary">Notifications</div>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">Email outbox</h1>
          <p className="mt-1 text-sm text-muted-foreground">Welcome, alert and weekly digest emails. Preview the HTML for each — they're queued right now.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" data-testid="digest-send-btn" onClick={sendDigest} disabled={busy} className="rounded-full"><Send className="mr-1 h-3 w-3" />Send digest now</Button>
          <Button size="sm" variant="outline" data-testid="flush-btn" onClick={flush} disabled={busy} className="rounded-full"><RefreshCw className="mr-1 h-3 w-3" />Flush queued</Button>
        </div>
      </div>

      <Card className={`border-border/60 ${data.resend_configured ? "bg-primary/[0.05]" : "bg-card/60"}`}>
        <CardContent className="p-4 text-sm">
          {data.resend_configured ? (
            <span><Badge variant="outline" className="mr-2 signal-buy border">CONNECTED</Badge>Resend is configured — new emails send immediately.</span>
          ) : (
            <span><Badge variant="outline" className="mr-2 signal-hold border">NOT CONFIGURED</Badge>Add <code className="mx-1 rounded bg-secondary/60 px-1 py-0.5 font-mono text-xs">RESEND_API_KEY</code> to <code className="mx-1 rounded bg-secondary/60 px-1 py-0.5 font-mono text-xs">/app/backend/.env</code> and restart the backend to activate deliveries. All emails are being queued in the meantime.</span>
          )}
        </CardContent>
      </Card>

      {/* Digest preferences */}
      <Card className="border-border/60 bg-card/60">
        <CardHeader><CardTitle className="text-sm font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2"><Calendar className="h-4 w-4" />Weekly digest preferences</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-3">
              <Switch
                checked={prefs.enabled}
                onCheckedChange={(v) => savePrefs({ ...prefs, enabled: v })}
                data-testid="digest-toggle"
              />
              <span className="text-sm">{prefs.enabled ? "Sending weekly" : "Paused"}</span>
            </label>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Day</span>
              <Select value={String(prefs.weekday)} onValueChange={(v) => savePrefs({ ...prefs, weekday: parseInt(v) })}>
                <SelectTrigger data-testid="digest-day" className="w-36"><SelectValue /></SelectTrigger>
                <SelectContent>{WEEKDAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Hour · UTC</span>
              <Select value={String(prefs.hour_utc)} onValueChange={(v) => savePrefs({ ...prefs, hour_utc: parseInt(v) })}>
                <SelectTrigger data-testid="digest-hour" className="w-28"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-[300px]">{HOURS.map(h => <SelectItem key={h} value={String(h)}>{String(h).padStart(2,"0")}:00</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="text-xs text-muted-foreground">
              Next digest fires at <strong className="text-foreground">{WEEKDAYS[prefs.weekday]} {String(prefs.hour_utc).padStart(2,"0")}:00 UTC</strong>. You can also trigger it manually with "Send digest now".
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/60 bg-card/60">
        <CardHeader><CardTitle className="text-sm font-mono uppercase tracking-widest text-muted-foreground">Recent emails</CardTitle></CardHeader>
        <CardContent>
          {data.items.length === 0 ? (
            <div className="rounded-md border border-dashed border-border/60 p-8 text-center text-sm text-muted-foreground">
              <Mail className="mx-auto h-6 w-6" /><div className="mt-2">No emails yet. Trigger an alert or click "Send digest now".</div>
            </div>
          ) : (
            <div className="divide-y divide-border/60">
              {data.items.map(e => (
                <div key={e.id} data-testid={`outbox-${e.id}`} className="flex items-center justify-between py-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`uppercase text-[10px] ${kindColor(e.kind)}`}>{e.kind}</Badge>
                      <span className="text-sm font-semibold">{e.subject}</span>
                    </div>
                    <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{new Date(e.created_at).toLocaleString()}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-[10px] font-mono uppercase tracking-widest ${statusColor(e.status)}`}>{e.status}</span>
                    <Button size="sm" variant="outline" data-testid={`preview-${e.id}`} onClick={() => preview(e.id)}>Preview</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!previewHtml} onOpenChange={(o) => { if (!o) setPreviewHtml(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{previewSubject}</DialogTitle></DialogHeader>
          <div className="max-h-[70vh] overflow-y-auto rounded-md border border-border/60">
            <iframe title="preview" srcDoc={previewHtml || ""} className="h-[500px] w-full rounded-md" />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
